import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Hero from '../components/Hero';
import About from '../components/About';
import Footer from '../components/Footer';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Monitor, Globe, Wrench, ArrowRight } from 'lucide-react';

const ServiceCards = () => {
  const navigate = useNavigate();

  const services = [
    {
      icon: Monitor,
      title: 'Digital Signage',
      desc: 'Turn any TV into a smart display. Manage slideshows, live streams, weather, and announcements from anywhere.',
      link: '/services/signage',
      color: 'cyan',
      gradient: 'from-cyan-500/10 to-cyan-600/5',
      border: 'hover:border-cyan-500/50',
      shadow: 'hover:shadow-cyan-500/10',
    },
    {
      icon: Globe,
      title: 'Custom Websites',
      desc: 'Clean, fast, easy-to-manage websites. We handle hosting, maintenance, and updates — no clunky admin panels.',
      link: '/services/websites',
      color: 'emerald',
      gradient: 'from-emerald-500/10 to-emerald-600/5',
      border: 'hover:border-emerald-500/50',
      shadow: 'hover:shadow-emerald-500/10',
    },
    {
      icon: Wrench,
      title: 'IT Services',
      desc: 'Networks, repairs, security cameras, smart home setup, and more. Professional IT for homes and businesses.',
      link: '/services/it',
      color: 'violet',
      gradient: 'from-violet-500/10 to-violet-600/5',
      border: 'hover:border-violet-500/50',
      shadow: 'hover:shadow-violet-500/10',
    },
  ];

  const colorMap = {
    cyan: 'text-cyan-400',
    emerald: 'text-emerald-400',
    violet: 'text-violet-400',
  };

  const bgMap = {
    cyan: 'bg-cyan-500/10 group-hover:bg-cyan-500/20',
    emerald: 'bg-emerald-500/10 group-hover:bg-emerald-500/20',
    violet: 'bg-violet-500/10 group-hover:bg-violet-500/20',
  };

  const btnMap = {
    cyan: 'text-cyan-400 hover:text-cyan-300',
    emerald: 'text-emerald-400 hover:text-emerald-300',
    violet: 'text-violet-400 hover:text-violet-300',
  };

  return (
    <section className="py-24 lg:py-32 bg-slate-950">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            What We <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">Do</span>
          </h2>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            From digital signage to custom websites to full IT support — we&apos;ve got your tech covered.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {services.map((s, i) => (
            <Card
              key={i}
              className={`group bg-gradient-to-br ${s.gradient} bg-slate-900/50 border-slate-800 ${s.border} transition-all duration-300 ${s.shadow} hover:shadow-xl hover:-translate-y-2 cursor-pointer`}
              onClick={() => navigate(s.link)}
            >
              <CardContent className="p-10 text-center space-y-5">
                <div className={`w-16 h-16 ${bgMap[s.color]} rounded-2xl flex items-center justify-center mx-auto transition-colors duration-300`}>
                  <s.icon className={`w-8 h-8 ${colorMap[s.color]}`} />
                </div>
                <h3 className="text-xl font-bold text-white group-hover:text-white transition-colors">{s.title}</h3>
                <p className="text-slate-400 leading-relaxed text-sm">{s.desc}</p>
                <button className={`${btnMap[s.color]} font-medium text-sm flex items-center gap-1 mx-auto group-hover:gap-2 transition-all`}>
                  Learn more <ArrowRight className="w-4 h-4" />
                </button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

const LandingPage = () => {
  return (
    <div className="min-h-screen bg-slate-950">
      <Header />
      <Hero />
      <ServiceCards />
      <About />
      <Footer />
    </div>
  );
};

export default LandingPage;
